import { Input, Output, EventEmitter, Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { AutoSub, AutoSubOnDestroy, StatsService } from '../../providers'
import { TransportationService } from './transportation.service';
import { JourneyPoint, JourneyType, JourneyPointType, JourneyRoute, Journey } from './transportation.model';
import { DateSelectComponent } from '../../components/date-select/date-select.component';
import { Coords, StatEventJourney } from '../../models';
import { JourneyDetails } from '../transportation/transportation.service';

@AutoSubOnDestroy()
@Component({
    selector: 'transportation',
    templateUrl: './transportation.component.html',
    styleUrls: ['./transportation.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class TransportationComponent implements OnInit {
    @ViewChild('DateSelect', { static: true }) dateSelect: DateSelectComponent;

    @Input() startPoint: JourneyPoint = null;
    @Input() endPoint: JourneyPoint = null;
    @Output() journeyChanged = new EventEmitter<Journey>();
    @Output() journeyPointClicked = new EventEmitter<JourneyPoint>();
    @Output() loading = new EventEmitter<Boolean>();
    @Output() noJourneyFound = new EventEmitter<string>();

    static readonly MIN_JOURNEY_DISTANCE_METERS = 600;

    private selectedDate = new Date();
    public dateStr = "";

    public plan: any[] = null;
    public journey: Journey = null;
    public planLoading: boolean = false;
    public detailsLoading: boolean = true;
    public awaitingInput: boolean = false;
    public noPlanAvailable: boolean = false;
    public showShortJourney: boolean = false;

    public minifyPlanHeader: boolean = false;

    // Export to view
    JourneyType = JourneyType;

    constructor(private transportationService: TransportationService, private statsService: StatsService) { }

    ngOnInit() {
        const debounce = debounceTime<Date>(1000);
        // Get all date selet events immediately
        AutoSub(this).reg['date'] = this.dateSelect.dateChanged.subscribe(date => {
            if(!this.startPoint || !this.endPoint) return;
            this.awaitingInput = true;
            this.noPlanAvailable = false;
        });

        // Chnage plan based on debounced date select events
        AutoSub(this).reg['dateDebounced'] = this.dateSelect.dateChanged.pipe(debounce).subscribe(date => {
            if(!this.startPoint || !this.endPoint) return;
            this.setLoading(true);
            this.selectedDate = date;
            // Load journey plan
            this.showJourney(this.startPoint.name, this.startPoint.coords, this.endPoint.name, this.endPoint.coords);
        });
    }

    public initJourney(startPoint: JourneyPoint, endPoint: JourneyPoint, resetStart: boolean = false) {
        // Reset journey if we got a new destibation (dont change when start point changes)
        if(resetStart || !this.endPoint || (this.endPoint.name != endPoint.name)) {
            this.resetJourney();
            this.journeyChanged.emit(null);
        }

        this.startPoint = startPoint;
        this.endPoint = endPoint;

        // Calculate distance between start and end
        let distance = Coords.calcDistance(this.startPoint.coords, this.endPoint.coords);
        this.showShortJourney = distance <= TransportationComponent.MIN_JOURNEY_DISTANCE_METERS;

        // Dont lookup plan if distance is below min, just show a walking route
        if(this.showShortJourney) {
            this.journey = this.createWalkingJourney(startPoint, endPoint, distance);
            this.plan = [];
        }

        if(!this.journey) {
            this.awaitingInput = true;
            this.setLoading(true);
            this.noPlanAvailable = false;
            this.showJourney(this.startPoint.name, this.startPoint.coords, this.endPoint.name, this.endPoint.coords);
        }
        else {
            // Emit last journey (with journey details)
            this.journeyChanged.emit(this.journey);
        }
    }

    private createWalkingJourney(start: JourneyPoint, end: JourneyPoint, distance: number): Journey {
        // Create route
        let route = new JourneyRoute();
        route.type = JourneyType.WALK;
        route.name = $localize`Its very close, you can walk`;
        route.start = start.coords;
        route.end = end.coords;
        route.points.push(start, end);

        // Create journey with route
        let journey = new Journey();
        journey.routes = [route];
        journey.duration = Math.round((distance / 100));
        journey.distance = distance;
        return journey;
    }

    public resetJourney() {
        this.plan = null;
        this.journey = null;
    }

    public showJourney(from: string, startPos: Coords, to: string, endPos: Coords) {
        this.awaitingInput = false;
        this.noPlanAvailable = false;
        this.detailsLoading = true;
        let date = this.selectedDate;

        // Lookup journey from rejseplanen
        this.transportationService.lookupJourney(from, startPos, to, endPos, date).then(async (data) => {
            let error = "";
            // Extract first leg of plan
            this.plan = data?.TripList?.Trip[0]?.Leg || null;
            // Pack plan legs with only one entry in array to unify structure (fixes #522)
            if(this.plan && !this.plan.length) this.plan = [this.plan];

            // Add stats event
            this.statsService.addStatsEventAddition(new StatEventJourney({ from: from, to: to, err: error }))

            // Dont do anything more if plan is not available
            if(!this.plan) {
                this.onJourneyError("No plan available");
                return;
            }

            // Extract start and end position of plan
            let start = this.plan[0].Origin;
            let end = this.plan[this.plan.length - 1].Destination;

            // Parse time stamps from start and end
            let startTime = TransportationComponent.parseTimestamp(start.date, start.time);
            let endTime = TransportationComponent.parseTimestamp(end.date, end.time);

            // Calculate duration in minutes
            let duration = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

            // Inject duration in first plan entry (consider making a plan object for this)
            this.plan[0].duration = duration;
            this.plan[0].durationHours = Math.floor(duration / 60);
            this.plan[0].durationMin = duration % 60;
            this.setLoading(false);

            // Convert journey type of all transportation types in plan
            for(let trans of this.plan) {
                trans.type = this.convertJourneyType(trans.type);
            }

            // Request journey details of all 
            let routes = await Promise.all(this.plan.map((trans) => this.loadJourneyDetails(trans, false)));
            this.populateJourneyStartEnd(routes, startPos, endPos);

            // Create journey object
            this.journey = new Journey();
            this.journey.routes = routes;
            this.journey.duration = duration;
            this.journeyChanged.emit(this.journey);

            // Update external loading indicator
            this.detailsLoading = false;
            this.setLoading(false);
        }).catch(err => this.onJourneyError(err));
    }

    private onJourneyError(err) {
        console.info(err);
        this.journey = null;
        this.awaitingInput = false;
        this.noPlanAvailable = true;
        this.detailsLoading = false;
        this.setLoading(false);
        this.noJourneyFound.emit("No journey plan available");
    }

    public onOriginClick(transOrigin: any, idx: number) {
        transOrigin.info = $localize`Showing on map...`;

        if(this.journey && this.journey.routes.length > idx && this.journey.routes[idx].start) {
            let route = this.journey.routes[idx];

            this.journeyPointClicked.emit(route.startPoint());
            transOrigin.info = "";
        }
        else {
            console.log("Journey point not available");
            transOrigin.info = $localize`(Unknown position)`
        }
    }

    public onDestinationClick(transDestination: any, idx: number) {
        transDestination.info = $localize`Showing on map...`;

        if(this.journey && this.journey.routes.length > idx && this.journey.routes[idx].end) {
            let route = this.journey.routes[idx];
            this.journeyPointClicked.emit(route.endPoint());
            transDestination.info = "";
        }
        else {
            console.log("Journey point not available");
            transDestination.info = $localize`(Unknown position)`
        }
    }

    private populateJourneyStartEnd(routeList: JourneyRoute[], startPos: Coords, endPos: Coords) {
        if(!routeList) return;

        routeList.forEach((route, idx) => {
            // Augment list with start pos
            if(idx == 0) {
                route.start = startPos;
                route.end = this.getNextRoutePos(routeList, idx);
            }
            // Augment last route in list
            else if(idx == (routeList.length - 1)) {
                // Find the previous 
                route.start = this.getPreviousRoutePos(routeList, idx);
                route.end = endPos;
            }
            else {
                route.start = route.points.length > 0 ? route.points[0].coords : this.getPreviousRoutePos(routeList, idx);
                route.end = route.points.length > 0 ? route.points[route.points.length - 1].coords : this.getNextRoutePos(routeList, idx);
            }

            // Nothing more to do if route points are already populated
            if(route.points.length > 0 || !route.start || !route.end) return;

            // Add start and end to journey route points (for Walk and other routes without details)
            // In case route has details, start and end journey points will be overwritten
            let pointType = this.deducePointType(route.type);
            let place = "";
            let name = (idx == 0) ? $localize`Start Point` : route.name;
            let label = route.name == $localize`Walk` ? $localize`W` : route.name;

            // Gen start point
            let startPoint = new JourneyPoint(name, label, place, route.start, pointType, route.type);
            this.startPoint.isStartPoint = true;
            this.startPoint.isEndPoint = false;

            // Gen end point
            let endPoint = new JourneyPoint($localize`End Point`, label, place, route.end, pointType, route.type);
            endPoint.isStartPoint = false;
            endPoint.isEndPoint = true;

            route.points.push(...[startPoint, endPoint]);
        });
    }

    private getNextRoutePos(routeList: JourneyRoute[], idx: number): Coords | null {
        if(!routeList) return;

        // Find the following route from list
        let nextJourney = (routeList.length > idx) ? routeList[idx + 1] : null;
        // Find the first point of next route
        let nextPoint = (nextJourney && nextJourney.points && nextJourney.points.length > 0) ? nextJourney.points[0] : null;
        return nextPoint ? nextPoint.coords : null;
    }

    private getPreviousRoutePos(routeList: JourneyRoute[], idx: number): Coords | null {
        if(!routeList) return;

        // Find the following route from list
        let prevJourney = (routeList.length > idx - 1) ? routeList[idx - 1] : null;
        // Find the last point of previous route
        let prevPoint = (prevJourney && prevJourney.points && prevJourney.points.length > 0) ? prevJourney.points[prevJourney.points.length - 1] : null;
        return prevPoint ? prevPoint.coords : null;
    }

    private convertJourneyType(type: string): JourneyType {
        switch(type) {
            case 'WALK': return JourneyType.WALK;
            case 'BUS': return JourneyType.BUS;
            case 'NB': return JourneyType.NIGHT_BUS;
            case 'TB': return JourneyType.TELE_BUS;
            case 'M': return JourneyType.METRO;
            case 'REG': return JourneyType.REG_TRAIN;
            case 'TOG': return JourneyType.TRAIN;
            case 'S': return JourneyType.S_TRAIN;
            case 'IC': return JourneyType.IC_TRAIN;
            case 'EXB': return JourneyType.EXTRA_BUS;
            case 'LYN': return JourneyType.FAST_TRAIN;
            case 'F': return JourneyType.FERRY;
            default: {
                console.warn("Unknown transportation type: ", type);
                return JourneyType.UNKNOWN
            };
        }
    }

    public static parseTimestamp(dateStr: string, timeStr: string): Date {
        let date = new Date();

        // Parse date str of format "DD.MM.YY"
        let day = dateStr.split(".");
        if(day.length < 3) {
            console.warn("Invalid date stamp in plan, could not parse date");
            return date;
        }

        // Parse time str of format "HH:MM"
        let time = timeStr.split(":");
        if(time.length <= 0) {
            console.warn("Invalid time stamp in plan, could not parse date");
            return date;
        }

        // Set parse date, month and year        
        date.setDate(parseInt(day[0]));
        date.setMonth(parseInt(day[1]) - 1);
        date.setFullYear(2000 + parseInt(day[2]));

        // Set parsed hours and minutes
        date.setHours(parseInt(time[0]));
        date.setMinutes(parseInt(time[1]));

        return date;
    }

    public async loadJourneyDetails(transInfo: any, showDetails = true): Promise<JourneyRoute> {
        if(!transInfo) return null;

        // Create a new journey route object with this part of journey
        let route = new JourneyRoute();
        route.type = transInfo.type;
        route.name = (transInfo.type == JourneyType.WALK) ? $localize`Walk` : transInfo.line || transInfo.name;

        if(!transInfo.JourneyDetailRef) {
            // Return route without details (ie. walk)
            return route;
        }

        let journeyRef = transInfo.JourneyDetailRef;
        journeyRef.loading = true;

        let details: JourneyDetails = await this.transportationService.lookupJourneyDetails(journeyRef.ref).catch(() => null);
        journeyRef.details = details;
        let stops: Array<any> = details?.JourneyDetail?.Stop;

        if(!stops) {
            journeyRef.routeStops = [];
            journeyRef.loading = false;
            return route;
            // { name: $localize`Unknown Stop`, coords: null };
        }

        // Find start and stop in list of all stops of this transportation stop list
        // Always find the last index of a entry as some routes (like the Metro) might be circular and contain duplicated names
        let startIdx = stops.map(stop => stop.name).lastIndexOf(transInfo.Origin.name);
        let endIdx = stops.map(stop => stop.name).lastIndexOf(transInfo.Destination.name);

        // Filter out irelevant stops from list
        journeyRef.routeStops = stops.slice(startIdx, endIdx + 1);

        journeyRef.loading = false;
        transInfo.JourneyDetailRef.showDetails = showDetails;

        // Create journey route object for external usage (draw on map)
        route.points = journeyRef.routeStops.map((details, idx) => {
            let pos = new Coords(parseInt(details.y) / 1000000, parseInt(details.x) / 1000000);
            let place = details.name;
            let type = this.deducePointType(route.type);
            let name = this.genPointName(type, route.name);
            let label = route.name;

            let point = new JourneyPoint(name, label, place, pos, type, route.type);
            // Indicate if this is a start point or end point
            point.isStartPoint = (idx == 0);
            point.isEndPoint = (idx == journeyRef.routeStops.length - 1);
            // Add point to points list in route
            return point;
        });
        return route;
    }

    /**
    * Deduce the point type from the JourneyType (reduce the number of different types)
    * ie. NIGHT_BUS, EXTRA_BUS => BUS_STOP
    */
    private deducePointType(journeyType: JourneyType): JourneyPointType {
        switch(journeyType) {
            case JourneyType.WALK:
                return JourneyPointType.WALK_POINT;

            case JourneyType.BUS:
            case JourneyType.EXTRA_BUS:
            case JourneyType.NIGHT_BUS:
                return JourneyPointType.BUS_STOP;

            case JourneyType.METRO:
                return JourneyPointType.METRO_STATION;

            case JourneyType.TRAIN:
            case JourneyType.REG_TRAIN:
            case JourneyType.S_TRAIN:
            case JourneyType.FAST_TRAIN:
            case JourneyType.IC_TRAIN:
                return JourneyPointType.TRAIN_STATION;

            case JourneyType.FERRY:
                return JourneyPointType.FERRY_STOP;

            case JourneyType.OTHER:
            case JourneyType.UNKNOWN:
            default:
                return JourneyPointType.OTHER_POINT;
        }
    }

    public genPointName(type: JourneyPointType, line: string): string {
        switch(type) {
            case JourneyPointType.WALK_POINT: return $localize`Walk`;
            case JourneyPointType.BUS_STOP: return $localize`Bus ` + line;
            case JourneyPointType.METRO_STATION: return $localize`Subway ` + line;
            case JourneyPointType.TRAIN_STATION: return $localize`Train ` + line;
            case JourneyPointType.FERRY_STOP: return $localize`Ferry ` + line;

            case JourneyPointType.OTHER_POINT:
            default: return $localize`Other`;
        }
    }

    public dateChanged(ev: Date) {
        // console.log("Event ", ev);
    }

    private setLoading(loading: boolean) {
        this.planLoading = loading;
        this.loading.emit(loading || this.detailsLoading);
    }

    public onPlanScroll(ev) {
        if(ev.srcElement.scrollTop > 10) {
            this.minifyPlanHeader = true;
        }
        else this.minifyPlanHeader = false;

    }

    public minifyHeader(minify) {
        this.minifyPlanHeader = minify;
    }

    public startJourney() {
        if(!this.journey || !this.journey.routes) return;
        if(this.journey.routes[1] && this.journey.routes[1]) this.journeyPointClicked.emit(this.journey.routes[1].points[0]);
        else this.journeyPointClicked.emit(this.journey.routes[0].startPoint());
    }

    ngOnDestroy() {
        console.log("Destroy transportation");
    }
}
